/**
 * PaaS（上海超算）门户对接前端工具。
 *
 * 文档来源：
 *  - PaaS 门户对接 API 文档（2026-06-17）
 *  - PaaS A1 答复：复用 permission.js 中智算平台跳转逻辑；
 *    NCNP→长三角商品详情/订购页后**前端静默触发**一次 SSO，
 *    不要出现页面实际跳转。
 *
 * 工作原理：
 *  - PaaS landing URL（type=Shanghai）通过 token 完成"长三角 user ↔ PaaS user_code" SSO 副作用；
 *  - 我们用一个隐藏 iframe 加载这个 URL，加载完成后立即移除。
 *  - 前端无法读取 iframe 跨域内容（也不需要读），SSO 副作用在 PaaS 服务端完成即可。
 *  - 浏览器需允许第三方 cookie（PaaS 联调阶段确认）。
 *
 * 触发时机（建议）：
 *  - 商品详情页 / 订购页路由首次加载（路由 meta.requirePaasSso=true 由 permission.js 兜底统一处理）；
 *  - 或由具体页面在 mounted 钩子调用 triggerPaasSilentSso()。
 *
 * 注意：
 *  - 同一会话内反复触发会重复建立 SSO 副作用，性能浪费但功能无害；
 *    本模块内置一次会话级缓存，相同 token 不重复发请求。
 *  - 静默 SSO 失败不阻断业务流程（PaaS 后续接口会以未授权拒绝，由各接口告警 + 引导重试）。
 */
import website from "@/config/website";
import { getToken } from "@/utils/auth";

const DEFAULT_PAAS_LANDING = "https://ai.ssc.net.cn:8443/outer/landing";
const SILENT_REDIRECT_TARGET = "silent";
const IFRAME_TIMEOUT_MS = 5000;

const sessionState = {
  /** 记录已为哪个 token 触发过 SSO，避免重复请求 */
  lastTriggeredToken: null,
};

/**
 * 拼装 PaaS landing URL。
 *
 * @param {string} token 当前长三角用户 token
 * @returns {string}
 */
function buildSilentLandingUrl(token) {
  const base = (website && website.paasLandingUrl) || DEFAULT_PAAS_LANDING;
  const params = new URLSearchParams();
  params.append("type", "Shanghai");
  params.append("redirect_target", SILENT_REDIRECT_TARGET);
  if (token) {
    params.append("token", token);
  }
  const sep = base.indexOf("?") >= 0 ? "&" : "?";
  return `${base}${sep}${params.toString()}`;
}

/**
 * 触发一次 PaaS 静默 SSO（隐藏 iframe）。
 *
 * @param {Object} [opts]
 * @param {boolean} [opts.force] 强制重发，忽略会话缓存
 * @returns {Promise<boolean>} resolve(true) 表示 iframe load 成功；resolve(false) 表示超时或异常（不抛错）
 */
export function triggerPaasSilentSso(opts) {
  const force = opts && opts.force === true;
  const token = getToken();
  if (!token) {
    return Promise.resolve(false);
  }
  if (!force && sessionState.lastTriggeredToken === token) {
    return Promise.resolve(true);
  }
  return new Promise((resolve) => {
    let settled = false;
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.setAttribute("aria-hidden", "true");
    iframe.setAttribute("tabindex", "-1");
    iframe.src = buildSilentLandingUrl(token);

    const cleanup = (ok) => {
      if (settled) return;
      settled = true;
      sessionState.lastTriggeredToken = ok ? token : sessionState.lastTriggeredToken;
      try {
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
      } catch (e) {
        // ignore
      }
      resolve(ok);
    };

    iframe.onload = () => cleanup(true);
    iframe.onerror = () => cleanup(false);
    setTimeout(() => cleanup(false), IFRAME_TIMEOUT_MS);

    try {
      document.body.appendChild(iframe);
    } catch (e) {
      cleanup(false);
    }
  });
}

/**
 * 重置 PaaS SSO 会话缓存（用户登出 / 切换账号时调用）。
 */
export function resetPaasSilentSsoCache() {
  sessionState.lastTriggeredToken = null;
}
